import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('communitySessionToken');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/auth/me`, {
        headers,
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        localStorage.setItem('userEmail', data.email);
        localStorage.setItem('userName', data.name);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { checkAuth(); }, []);

  const logout = async () => {
    const token = localStorage.getItem('communitySessionToken');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    await fetch(`${API}/api/auth/logout`, {
      method: 'POST',
      headers,
    }).catch(() => {});
    localStorage.removeItem('communitySessionToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userToken');
    setUser(null);
  };

  return { user, loading, checkAuth, logout, setUser };
}

export function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const location = useLocation();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Check for Emergent auth hash callback (legacy)
    const hash = window.location.hash;
    const hashMatch = hash.match(/session_id=([^&]+)/);
    if (hashMatch) {
      const sessionId = hashMatch[1];
      (async () => {
        try {
          const res = await fetch(`${API}/api/auth/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionId }),
          });
          if (res.ok) {
            const data = await res.json();
            localStorage.setItem('communitySessionToken', data.session_token);
            localStorage.setItem('userEmail', data.email);
            localStorage.setItem('userName', data.name);
            if (data.credits !== undefined) localStorage.setItem('userCredits', data.credits);
            if (data.plan) localStorage.setItem('userPlan', data.plan);
            if (data.needs_profile) navigate('/complete-profile', { replace: true, state: { user: data } });
            else if (data.is_new_user) navigate('/choose-plan', { replace: true });
            else navigate('/my-solutions', { replace: true });
          } else { setError('Error de sesion'); setTimeout(() => navigate('/login', { replace: true }), 2000); }
        } catch { setError('Error de conexion'); setTimeout(() => navigate('/login', { replace: true }), 2000); }
      })();
      return;
    }

    // Check for Google OAuth code callback
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if ((params.get('state') || '').startsWith('mtr_')) {
      window.location.replace('/api/journal-access/callback?' + params.toString());
      return;
    }
    if (code) {
      (async () => {
        try {
          // Use the current origin — callback always runs on the same domain as the app,
          // so this matches exactly what was sent to Google at the auth start.
          const redirectUri = window.location.origin + '/auth/google/callback';
          console.log('[AuthCallback] Exchanging code, redirect_uri:', redirectUri);
          const res = await fetch(`${API}/api/auth/google/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, redirect_uri: redirectUri }),
          });
          if (res.ok) {
            const data = await res.json();
            console.log('[AuthCallback] Google auth success:', data.email);
            localStorage.setItem('communitySessionToken', data.session_token);
            localStorage.setItem('userEmail', data.email);
            localStorage.setItem('userName', data.name);
            if (data.credits !== undefined) localStorage.setItem('userCredits', data.credits);
            if (data.plan) localStorage.setItem('userPlan', data.plan);
            if (data.needs_profile) navigate('/complete-profile', { replace: true, state: { user: data } });
            else if (data.is_new_user) {
              localStorage.setItem('showTourOnDashboard', 'true');
              navigate('/choose-plan', { replace: true });
            } else navigate('/my-solutions', { replace: true });
          } else {
            const errData = await res.json().catch(() => ({}));
            console.error('[AuthCallback] Google auth failed:', res.status, errData);
            setError(errData.detail || 'Error al autenticar con Google');
            setTimeout(() => navigate('/login', { replace: true }), 3000);
          }
        } catch (e) {
          console.error('[AuthCallback] Google auth exception:', e);
          setError('Error de conexion: ' + e.message);
          setTimeout(() => navigate('/login', { replace: true }), 3000);
        }
      })();
      return;
    }

    navigate('/login', { replace: true });
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
              <span className="text-red-400 text-lg">!</span>
            </div>
            <p className="text-sm text-red-400 mb-1">{error}</p>
            <p className="text-xs text-white/40">Redirigiendo al login...</p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-[#009EB5] mx-auto mb-3" />
            <p className="text-sm text-white/60">Autenticando con Google...</p>
          </>
        )}
      </div>
    </div>
  );
}

export function GoogleLoginButton({ className = '', label = 'Continuar con Google' }) {
  const handleLogin = () => {
    const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!GOOGLE_CLIENT_ID) {
      alert('Google OAuth no está configurado. Configure REACT_APP_GOOGLE_CLIENT_ID.');
      return;
    }
    // Always use the current origin for redirect - handles both www and non-www,
    // preview, and production. The callback on the same domain will regenerate the
    // exact same URI, so no localStorage dependency is needed.
    const redirectUri = window.location.origin + '/auth/google/callback';
    const scope = 'openid email profile';
    const googleUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
    window.location.href = googleUrl;
  };

  return (
    <button
      onClick={handleLogin}
      className={`flex items-center justify-center gap-3 w-full px-4 py-3 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm rounded-xl border border-gray-200 transition-all hover:shadow-md ${className}`}
      data-testid="google-login-btn"
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
      </svg>
      {label}
    </button>
  );
}
